import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  LTE_METRIC_THRESHOLDS,
  SPEED_METRIC_THRESHOLDS,
  metricQuality,
} from '../lte-band-section/LteBandSection';

const CHART_W = 640;
const CHART_H = 220;
const PAD_LINE = { top: 16, right: 16, bottom: 28, left: 44 };
const PAD_BARS = { top: 28, right: 16, bottom: 28, left: 44 };

/** Пороги качества → горизонтальные линии (RF + скорость). */
const buildQualityGuideLines = (metric) => {
  if (metric === 'download' || metric === 'upload') {
    const thresholds = SPEED_METRIC_THRESHOLDS[metric];
    return [
      {
        key: 'excellent',
        value: thresholds.excellent,
        label: 'отлично',
        tone: 'ok',
      },
      {
        key: 'good',
        value: thresholds.good,
        label: 'среднее',
        tone: 'warn',
      },
    ];
  }
  if (metric !== 'rsrp' && metric !== 'snr') return [];
  const thresholds = LTE_METRIC_THRESHOLDS[metric];
  if (!thresholds) return [];
  return [
    {
      key: 'excellent',
      value: thresholds.excellent,
      label: 'отлично',
      tone: 'ok',
    },
    {
      key: 'good',
      value: thresholds.good,
      label: 'хорошо',
      tone: 'warn',
    },
  ];
};

const expandDomainWithGuides = (domain, guideValues) => {
  if (!guideValues.length) return domain;
  const all = [domain.min, domain.max, ...guideValues].filter((value) => Number.isFinite(value));
  if (!all.length) return domain;
  let min = Math.min(...all);
  let max = Math.max(...all);
  if (min === max) {
    const pad = Math.abs(min) * 0.1 || 1;
    min -= pad;
    max += pad;
  } else {
    const pad = (max - min) * 0.08;
    min -= pad;
    max += pad;
  }
  return { min, max };
};

const metricUnit = (metric) => {
  if (metric === 'rsrp' || metric === 'snr') return '';
  return ' Мбит/с';
};

const formatValue = (value, metric) => {
  if (value == null || Number.isNaN(Number(value))) return '—';
  const num = Number(value);
  if (metric === 'rsrp' || metric === 'snr') return String(Math.round(num * 10) / 10);
  return String(Math.round(num * 100) / 100);
};

const formatTooltipValue = (value, metric) => {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return `${formatValue(value, metric)}${metricUnit(metric)}`.trim();
};

const parseTs = (value) => {
  if (value == null || value === '') return null;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const ms = Date.parse(String(value));
  return Number.isNaN(ms) ? null : ms;
};

const formatAxisTime = (ms, spanMs) => {
  const date = new Date(ms);
  if (!Number.isFinite(ms)) return '';
  if (spanMs > 3 * 24 * 60 * 60 * 1000) {
    return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
  }
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const seriesHasBucketValues = (series) => (
  (series || []).some((item) => Array.isArray(item.values) && item.values.length > 0)
);

const computeYDomain = (series, useBuckets) => {
  const values = [];
  series.forEach((item) => {
    if (useBuckets) {
      (item.values || []).forEach((value) => {
        if (value != null && !Number.isNaN(Number(value))) values.push(Number(value));
      });
    } else {
      (item.points || []).forEach((point) => {
        if (point.v != null && !Number.isNaN(Number(point.v))) values.push(Number(point.v));
      });
    }
  });
  if (!values.length) return { min: 0, max: 1 };
  let min = Math.min(...values);
  let max = Math.max(...values);
  if (min === max) {
    const pad = Math.abs(min) * 0.1 || 1;
    min -= pad;
    max += pad;
  } else {
    const pad = (max - min) * 0.08;
    min -= pad;
    max += pad;
  }
  return { min, max };
};

const buildLinePath = (points, xAtTime, yAt) => {
  const usable = (points || [])
    .map((point) => ({ point, ms: parseTs(point.t) }))
    .filter(({ point, ms }) => ms != null && point.v != null && !Number.isNaN(Number(point.v)))
    .sort((a, b) => a.ms - b.ms);
  if (!usable.length) return '';
  return usable
    .map(({ point, ms }, idx) => {
      const x = xAtTime(ms);
      const y = yAt(Number(point.v));
      return `${idx === 0 ? 'M' : 'L'}${x},${y}`;
    })
    .join(' ');
};

const buildBucketLinePath = (values, xAtIndex, yAt) => {
  const usable = (values || [])
    .map((value, index) => ({ value, index }))
    .filter(({ value }) => value != null && !Number.isNaN(Number(value)));
  if (!usable.length) return '';
  return usable
    .map(({ value, index }, idx) => {
      const x = xAtIndex(index);
      const y = yAt(Number(value));
      return `${idx === 0 ? 'M' : 'L'}${x},${y}`;
    })
    .join(' ');
};

const resolveBucketIndexFromOverlay = (offsetX, overlayWidth, pad, chartW, bucketCount, mode = 'line') => {
  if (overlayWidth <= 0 || bucketCount <= 0) return 0;
  const svgX = (offsetX / overlayWidth) * chartW;
  const plotW = chartW - pad.left - pad.right;
  if (plotW <= 0) return 0;
  const plotX = svgX - pad.left;
  if (bucketCount <= 1) return 0;
  if (mode === 'bar') {
    const groupWidth = plotW / bucketCount;
    return Math.max(0, Math.min(bucketCount - 1, Math.floor(plotX / groupWidth)));
  }
  const ratio = Math.max(0, Math.min(1, plotX / plotW));
  return Math.round(ratio * (bucketCount - 1));
};

/** Какой бар (band) под курсором внутри bucket — layout как у отрисовки bars. */
const resolveBarSeriesIdFromOverlay = (
  offsetX,
  overlayWidth,
  pad,
  chartW,
  bucketIndex,
  bucketCount,
  seriesList,
) => {
  if (!Array.isArray(seriesList) || seriesList.length === 0) return null;
  if (overlayWidth <= 0 || bucketCount <= 0) return seriesList[0].id;
  const svgX = (offsetX / overlayWidth) * chartW;
  const plotW = chartW - pad.left - pad.right;
  const groupWidth = plotW / bucketCount;
  const bandCount = seriesList.length;
  const barWidth = Math.max(2, (groupWidth * 0.7) / bandCount);
  const groupX = pad.left + bucketIndex * groupWidth + groupWidth * 0.15;
  const relX = svgX - groupX;
  if (relX < 0) return seriesList[0].id;
  const bandIndex = Math.max(0, Math.min(bandCount - 1, Math.floor(relX / barWidth)));
  return seriesList[bandIndex]?.id ?? seriesList[0].id;
};

/**
 * График проверки локаций.
 * chartType: line|bar — отрисовка
 * aggregation: hourly|daily — ось/подписи
 * hoverBucketIndex / onHoverBucketChange — синхрон hover между графиками
 * tooltipGroups — секции tooltip (скорость + RF)
 */
const LocationCheckChart = ({
  chartType = 'line',
  aggregation = 'hourly',
  view = null,
  metric = 'download',
  labels = [],
  series = [],
  fromTs = null,
  toTs = null,
  tooltipDayLabel = null,
  height = CHART_H,
  ariaLabel = 'График проверки локаций',
  hoverBucketIndex = null,
  hoverSeriesId = null,
  hoverPointer = null,
  onHoverBucketChange = null,
  showTooltip = true,
  tooltipGroups = null,
  chartId = 'chart',
}) => {
  const [hiddenIds, setHiddenIds] = useState(() => new Set());
  const [localHover, setLocalHover] = useState(null);
  const plotOverlayRef = useRef(null);

  const resolvedChartType = chartType || (view === 'line' ? 'line' : 'bar');
  const resolvedAggregation = aggregation
    || (view === 'daily' || view === 'dow' ? 'daily' : 'hourly');

  const isHoverControlled = typeof onHoverBucketChange === 'function';
  const activeBucketIndex = isHoverControlled ? hoverBucketIndex : localHover?.bucketIndex ?? null;
  const activeSeriesId = isHoverControlled
    ? (hoverSeriesId ?? hoverPointer?.seriesId ?? null)
    : (localHover?.seriesId ?? null);

  const handleLegendToggle = useCallback((seriesId) => {
    setHiddenIds((prev) => {
      const next = new Set(prev);
      if (next.has(seriesId)) next.delete(seriesId);
      else next.add(seriesId);
      return next;
    });
  }, []);

  const handleLegendKeyDown = useCallback((event, seriesId) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    handleLegendToggle(seriesId);
  }, [handleLegendToggle]);

  const visibleSeries = useMemo(
    () => (series || []).filter((item) => !hiddenIds.has(item.id)),
    [series, hiddenIds],
  );

  const useBuckets = seriesHasBucketValues(visibleSeries) || (labels || []).length > 0;
  const isBars = resolvedChartType === 'bar';
  const PAD = isBars ? PAD_BARS : PAD_LINE;
  const plotW = CHART_W - PAD.left - PAD.right;
  const plotH = height - PAD.top - PAD.bottom;

  const qualityGuides = useMemo(() => buildQualityGuideLines(metric), [metric]);

  const domain = useMemo(
    () => expandDomainWithGuides(
      computeYDomain(visibleSeries, useBuckets),
      qualityGuides.map((guide) => guide.value),
    ),
    [visibleSeries, useBuckets, qualityGuides],
  );

  const timeDomain = useMemo(() => {
    let from = parseTs(fromTs);
    let to = parseTs(toTs);
    if (from == null || to == null || from >= to) {
      const all = [];
      visibleSeries.forEach((item) => {
        (item.points || []).forEach((point) => {
          const ms = parseTs(point.t);
          if (ms != null) all.push(ms);
        });
      });
      if (all.length) {
        from = Math.min(...all);
        to = Math.max(...all);
      } else {
        const now = Date.now();
        from = now - 24 * 60 * 60 * 1000;
        to = now;
      }
    }
    if (from === to) {
      from -= 60 * 60 * 1000;
      to += 60 * 60 * 1000;
    }
    return { from, to, span: to - from };
  }, [fromTs, toTs, visibleSeries]);

  const yAt = useCallback((value) => {
    const ratio = (value - domain.min) / (domain.max - domain.min || 1);
    return PAD.top + plotH - ratio * plotH;
  }, [domain, plotH, PAD.top]);

  const xAtTime = useCallback((ms) => {
    const ratio = (ms - timeDomain.from) / (timeDomain.span || 1);
    const clamped = Math.min(1, Math.max(0, ratio));
    return PAD.left + clamped * plotW;
  }, [timeDomain, plotW, PAD.left]);

  const bucketCount = Math.max(1, labels.length || 1);
  const groupWidth = plotW / bucketCount;

  const xAtIndex = useCallback((index) => (
    PAD.left + index * groupWidth + groupWidth / 2
  ), [PAD.left, groupWidth]);

  const xTicks = useMemo(() => {
    if (useBuckets || isBars) return [];
    const ticks = [];
    const count = 4;
    for (let i = 0; i < count; i += 1) {
      const ms = timeDomain.from + (timeDomain.span * i) / (count - 1);
      ticks.push(ms);
    }
    return ticks;
  }, [useBuckets, isBars, timeDomain]);

  const yTicks = useMemo(() => {
    const ticks = [];
    for (let i = 0; i < 4; i += 1) {
      const value = domain.min + ((domain.max - domain.min) * i) / 3;
      ticks.push(value);
    }
    return ticks;
  }, [domain]);

  const hasData = visibleSeries.some((item) => (
    useBuckets
      ? (item.values || []).some((v) => v != null)
      : (item.points || []).some((p) => p.v != null)
  ));

  const bandCount = Math.max(1, visibleSeries.length);
  const barWidth = Math.max(2, (groupWidth * 0.7) / bandCount);

  const axisLabels = useMemo(() => {
    const source = Array.isArray(labels) ? labels : [];
    if (resolvedAggregation === 'hourly') {
      return source.map((label, index) => (index % 3 === 0 ? label : ''));
    }
    if (resolvedAggregation === 'daily' && source.length > 16) {
      const step = Math.ceil(source.length / 16);
      return source.map((label, index) => (index % step === 0 ? label : ''));
    }
    return source;
  }, [labels, resolvedAggregation]);

  const handlePlotMouseMove = useCallback((event) => {
    if (!useBuckets) return;
    const overlay = plotOverlayRef.current;
    if (!overlay) return;
    const rect = overlay.getBoundingClientRect();
    if (rect.width <= 0) return;
    const offsetX = event.clientX - rect.left;
    const offsetY = event.clientY - rect.top;
    const bucketIndex = resolveBucketIndexFromOverlay(
      offsetX,
      rect.width,
      PAD,
      CHART_W,
      bucketCount,
      isBars ? 'bar' : 'line',
    );
    const seriesId = isBars
      ? resolveBarSeriesIdFromOverlay(
        offsetX,
        rect.width,
        PAD,
        CHART_W,
        bucketIndex,
        bucketCount,
        visibleSeries,
      )
      : null;
    const nextHover = {
      bucketIndex,
      seriesId,
      source: chartId,
      offsetX,
      offsetY,
      plotWidth: rect.width,
      plotHeight: rect.height,
    };
    if (isHoverControlled) {
      onHoverBucketChange(nextHover);
      return;
    }
    setLocalHover(nextHover);
  }, [
    useBuckets,
    PAD,
    bucketCount,
    isBars,
    visibleSeries,
    isHoverControlled,
    onHoverBucketChange,
    chartId,
  ]);

  const handlePlotMouseLeave = useCallback(() => {
    if (isHoverControlled) {
      onHoverBucketChange(null);
      return;
    }
    setLocalHover(null);
  }, [isHoverControlled, onHoverBucketChange]);

  const activeHoverPointer = isHoverControlled ? hoverPointer : localHover;

  const activeBarBandIndex = useMemo(() => {
    if (!isBars || !activeSeriesId) return -1;
    return visibleSeries.findIndex((item) => item.id === activeSeriesId);
  }, [isBars, activeSeriesId, visibleSeries]);

  const crosshairLeftPercent = useMemo(() => {
    if (activeBucketIndex == null) return 0;
    if (isBars && activeBarBandIndex >= 0) {
      const groupX = PAD.left + activeBucketIndex * groupWidth + groupWidth * 0.15;
      const x = groupX + activeBarBandIndex * barWidth + barWidth / 2;
      return (x / CHART_W) * 100;
    }
    return (xAtIndex(activeBucketIndex) / CHART_W) * 100;
  }, [
    activeBucketIndex,
    isBars,
    activeBarBandIndex,
    PAD.left,
    groupWidth,
    barWidth,
    xAtIndex,
  ]);

  const hoverMarkers = useMemo(() => {
    if (activeBucketIndex == null || !useBuckets || isBars) return [];
    return visibleSeries
      .map((item) => {
        const value = item.values?.[activeBucketIndex];
        if (value == null || Number.isNaN(Number(value))) return null;
        return {
          id: item.id,
          color: item.color || '#5283F4',
          x: xAtIndex(activeBucketIndex),
          y: yAt(Number(value)),
        };
      })
      .filter(Boolean);
  }, [activeBucketIndex, useBuckets, isBars, visibleSeries, xAtIndex, yAt]);

  const resolvedTooltipGroups = useMemo(() => {
    const baseGroups = Array.isArray(tooltipGroups) && tooltipGroups.length > 0
      ? tooltipGroups
      : [{
        id: 'main',
        title: null,
        metric,
        series: visibleSeries,
      }];
    // Бар: только выбранный band во всех секциях (Download + RSRP + SNR)
    if (!isBars || !activeSeriesId) {
      return baseGroups;
    }
    return baseGroups
      .map((group) => ({
        ...group,
        series: (group.series || []).filter((item) => item.id === activeSeriesId),
      }))
      .filter((group) => (group.series || []).length > 0);
  }, [tooltipGroups, metric, visibleSeries, isBars, activeSeriesId]);

  const tooltipRowCount = useMemo(
    () => resolvedTooltipGroups.reduce(
      (sum, group) => sum + (Array.isArray(group.series) ? group.series.length : 0),
      0,
    ),
    [resolvedTooltipGroups],
  );

  const bucketLabel = useMemo(() => {
    if (activeBucketIndex == null) return '';
    if (resolvedAggregation === 'hourly') {
      const raw = labels[activeBucketIndex];
      const hourMatch = String(raw ?? activeBucketIndex).match(/(\d{1,2})/);
      const hour = hourMatch ? Number(hourMatch[1]) : activeBucketIndex;
      const safeHour = Number.isFinite(hour) ? Math.max(0, Math.min(23, hour)) : activeBucketIndex;
      const timeLabel = `${String(safeHour).padStart(2, '0')}:00`;
      if (tooltipDayLabel) {
        return `${tooltipDayLabel}, ${timeLabel} МСК`;
      }
      // Неделя/месяц: среднее по этому часу суток за период
      return `${timeLabel} МСК · ср.`;
    }
    const dayLabel = labels[activeBucketIndex] || String(activeBucketIndex + 1);
    return `${dayLabel} МСК`;
  }, [activeBucketIndex, labels, resolvedAggregation, tooltipDayLabel]);

  const tooltipStyle = useMemo(() => {
    if (!showTooltip || activeBucketIndex == null) return null;
    const overlay = plotOverlayRef.current;
    const plotWidth = overlay?.getBoundingClientRect()?.width || 0;
    const plotHeight = overlay?.getBoundingClientRect()?.height || 0;
    if (plotWidth <= 0 || plotHeight <= 0) {
      return { left: 12, top: 12 };
    }
    const tooltipWidth = 180;
    const tooltipHeight = 28 + tooltipRowCount * 18 + resolvedTooltipGroups.length * 10;
    const anchorX = (crosshairLeftPercent / 100) * plotWidth;
    let left = anchorX + 12;
    if (left + tooltipWidth > plotWidth - 4) {
      left = anchorX - tooltipWidth - 12;
    }
    left = Math.max(4, Math.min(left, plotWidth - tooltipWidth - 4));
    const pointerY = activeHoverPointer?.offsetY;
    let top = typeof pointerY === 'number'
      ? pointerY - tooltipHeight / 2
      : 12;
    top = Math.max(4, Math.min(top, plotHeight - tooltipHeight - 4));
    return { left, top };
  }, [
    showTooltip,
    activeBucketIndex,
    tooltipRowCount,
    resolvedTooltipGroups.length,
    crosshairLeftPercent,
    activeHoverPointer,
  ]);

  const chartClassName = [
    'lc-chart',
    resolvedAggregation === 'hourly' ? 'lc-chart--x-hourly' : '',
    resolvedAggregation === 'daily' ? 'lc-chart--x-daily' : '',
    activeBucketIndex != null ? 'lc-chart--hover' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={chartClassName}>
      <div className="lc-chart__legend" role="list" aria-label="Серии (bands)">
        {(series || []).map((item) => {
          const hidden = hiddenIds.has(item.id);
          return (
            <button
              key={item.id}
              type="button"
              className={`lc-chart__legend-item${hidden ? ' lc-chart__legend-item--hidden' : ''}`}
              onClick={() => handleLegendToggle(item.id)}
              onKeyDown={(event) => handleLegendKeyDown(event, item.id)}
              aria-pressed={!hidden}
              tabIndex={0}
            >
              <span
                className="lc-chart__legend-swatch"
                style={{ background: item.color || '#5283F4' }}
                aria-hidden="true"
              />
              {item.label || item.id}
            </button>
          );
        })}
      </div>

      {!hasData ? (
        <p className="lc-chart__empty">Нет данных за выбранный период</p>
      ) : (
        <div className="lc-chart__plot">
          <svg
            className="lc-chart__svg"
            viewBox={`0 0 ${CHART_W} ${height}`}
            role="img"
            aria-label={ariaLabel}
          >
            {yTicks.map((tick) => (
              <g key={`yt-${tick}`}>
                <line
                  x1={PAD.left}
                  x2={CHART_W - PAD.right}
                  y1={yAt(tick)}
                  y2={yAt(tick)}
                  className="lc-chart__grid"
                />
                <text x={PAD.left - 6} y={yAt(tick) + 3} className="lc-chart__tick" textAnchor="end">
                  {formatValue(tick, metric)}
                </text>
              </g>
            ))}

            {qualityGuides.map((guide) => {
              const y = yAt(guide.value);
              if (y < PAD.top - 2 || y > PAD.top + plotH + 2) return null;
              return (
                <g key={`qg-${guide.key}`} className="lc-chart__quality-guide">
                  <line
                    x1={PAD.left}
                    x2={CHART_W - PAD.right}
                    y1={y}
                    y2={y}
                    className={`lc-chart__quality-line lc-chart__quality-line--${guide.tone}`}
                  />
                  <text
                    x={CHART_W - PAD.right - 4}
                    y={Math.max(PAD.top + 10, y - 4)}
                    className={`lc-chart__quality-label lc-chart__quality-label--${guide.tone}`}
                    textAnchor="end"
                  >
                    {guide.label}
                    {' '}
                    ≥
                    {' '}
                    {formatValue(guide.value, metric)}
                  </text>
                </g>
              );
            })}

            {qualityGuides.length > 0 ? (() => {
              const goodGuide = qualityGuides.find((guide) => guide.key === 'good');
              if (!goodGuide) return null;
              const goodY = yAt(goodGuide.value);
              const badLabelY = Math.min(PAD.top + plotH - 4, goodY + 14);
              if (badLabelY <= goodY + 2) return null;
              return (
                <text
                  key="qg-bad"
                  x={CHART_W - PAD.right - 4}
                  y={badLabelY}
                  className="lc-chart__quality-label lc-chart__quality-label--bad"
                  textAnchor="end"
                >
                  плохо
                </text>
              );
            })() : null}

            {!isBars && useBuckets && visibleSeries.map((item) => {
              const values = item.values || [];
              const path = buildBucketLinePath(values, xAtIndex, yAt);
              return (
                <g key={item.id}>
                  {path ? (
                    <path
                      d={path}
                      fill="none"
                      stroke={item.color || '#5283F4'}
                      strokeWidth="2"
                      strokeLinejoin="round"
                      strokeLinecap="round"
                    />
                  ) : null}
                  {values.map((value, index) => {
                    if (value == null || Number.isNaN(Number(value))) return null;
                    if (activeBucketIndex === index) return null;
                    return (
                      <circle
                        key={`${item.id}-bv-${index}`}
                        cx={xAtIndex(index)}
                        cy={yAt(Number(value))}
                        r="3"
                        fill={item.color || '#5283F4'}
                      />
                    );
                  })}
                </g>
              );
            })}

            {!isBars && !useBuckets && visibleSeries.map((item) => {
              const points = item.points || [];
              const path = buildLinePath(points, xAtTime, yAt);
              return (
                <g key={item.id}>
                  {path ? (
                    <path
                      d={path}
                      fill="none"
                      stroke={item.color || '#5283F4'}
                      strokeWidth="2"
                      strokeLinejoin="round"
                      strokeLinecap="round"
                    />
                  ) : null}
                  {points.map((point, index) => {
                    const ms = parseTs(point.t);
                    if (ms == null) return null;
                    const cx = xAtTime(ms);
                    if (point.v == null || Number.isNaN(Number(point.v))) {
                      if (point.status && point.status !== 'ok') {
                        return (
                          <circle
                            key={`${item.id}-m-${index}`}
                            cx={cx}
                            cy={PAD.top + plotH}
                            r="3.5"
                            fill="#F33F4E"
                          />
                        );
                      }
                      return null;
                    }
                    return (
                      <circle
                        key={`${item.id}-p-${index}`}
                        cx={cx}
                        cy={yAt(Number(point.v))}
                        r="3"
                        fill={item.color || '#5283F4'}
                      />
                    );
                  })}
                </g>
              );
            })}

            {isBars && visibleSeries.map((item, bandIndex) => (
              <g key={item.id}>
                {(item.values || []).map((value, index) => {
                  if (value == null || Number.isNaN(Number(value))) return null;
                  const groupX = PAD.left + index * groupWidth + groupWidth * 0.15;
                  const x = groupX + bandIndex * barWidth;
                  const y = yAt(Number(value));
                  const h = Math.max(1, PAD.top + plotH - y);
                  const bandLabel = item.label || item.id;
                  const labelY = Math.max(PAD.top - 6, y - 5);
                  const isActiveBar = activeBucketIndex === index
                    && (activeSeriesId == null || activeSeriesId === item.id);
                  return (
                    <g key={`${item.id}-b-${index}`}>
                      <rect
                        x={x}
                        y={y}
                        width={barWidth}
                        height={h}
                        rx="2"
                        fill={item.color || '#5283F4'}
                        opacity={activeBucketIndex == null || isActiveBar ? 1 : 0.35}
                      />
                      <text
                        x={x + barWidth / 2}
                        y={labelY}
                        className="lc-chart__bar-label"
                        textAnchor="middle"
                        fill={item.color || '#5283F4'}
                      >
                        {bandLabel}
                      </text>
                    </g>
                  );
                })}
              </g>
            ))}

            {hoverMarkers.map((marker) => (
              <circle
                key={`hm-${marker.id}`}
                cx={marker.x}
                cy={marker.y}
                r="4"
                fill="#ffffff"
                stroke={marker.color}
                strokeWidth="2"
              />
            ))}

            {(useBuckets ? axisLabels : []).map((label, index) => (
              label ? (
                <text
                  key={`xl-${label}-${index}`}
                  x={xAtIndex(index)}
                  y={height - 8}
                  className="lc-chart__tick lc-chart__tick--x"
                  textAnchor="middle"
                >
                  {label}
                </text>
              ) : null
            ))}

            {!useBuckets && !isBars && xTicks.map((ms) => (
              <text
                key={`xt-${ms}`}
                x={xAtTime(ms)}
                y={height - 8}
                className="lc-chart__tick"
                textAnchor="middle"
              >
                {formatAxisTime(ms, timeDomain.span)}
              </text>
            ))}
          </svg>

          {useBuckets ? (
            <div
              ref={plotOverlayRef}
              className={`lc-chart__interaction${activeBucketIndex != null ? ' lc-chart__interaction--active' : ''}`}
              onMouseMove={handlePlotMouseMove}
              onMouseLeave={handlePlotMouseLeave}
              role="presentation"
              aria-hidden="true"
            >
              {activeBucketIndex != null ? (
                <>
                  <div
                    className="lc-chart__crosshair lc-chart__crosshair--vertical"
                    style={{ left: `${crosshairLeftPercent}%` }}
                  />
                  {showTooltip && tooltipStyle ? (
                    <div className="lc-chart__tooltip" style={tooltipStyle}>
                      <p className="lc-chart__tooltip-title">{bucketLabel}</p>
                      {resolvedTooltipGroups.map((group) => (
                        <div key={group.id} className="lc-chart__tooltip-group">
                          {group.title ? (
                            <p className="lc-chart__tooltip-group-title">{group.title}</p>
                          ) : null}
                          <ul className="lc-chart__tooltip-list">
                            {(group.series || []).map((item) => {
                              const value = item.values?.[activeBucketIndex];
                              const rowMetric = group.metric || metric;
                              const qualityKind = group.qualityMetric
                                || (
                                  rowMetric === 'rsrp'
                                  || rowMetric === 'snr'
                                  || rowMetric === 'download'
                                  || rowMetric === 'upload'
                                    ? rowMetric
                                    : null
                                );
                              const quality = qualityKind ? metricQuality(qualityKind, value) : 'na';
                              const valueClass = quality && quality !== 'na'
                                ? `lc-chart__tooltip-value lc-chart__tooltip-value--${quality}`
                                : 'lc-chart__tooltip-value';
                              return (
                                <li key={`${group.id}-${item.id}`} className="lc-chart__tooltip-row">
                                  <span
                                    className="lc-chart__tooltip-dot"
                                    style={{ backgroundColor: item.color || '#5283F4' }}
                                    aria-hidden="true"
                                  />
                                  <span className="lc-chart__tooltip-label">
                                    {item.label || item.id}
                                  </span>
                                  <span className={valueClass}>
                                    {formatTooltipValue(value, rowMetric)}
                                  </span>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </>
              ) : null}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default LocationCheckChart;
